import React, { useState, useEffect } from 'react';
import { MemberService } from '../services/firebaseService';
import { EmailService } from '../services/emailService';
import { MagicLinkService } from '../services/magicLinkService';
import { Member } from '../types';
import ImportMembersModal from '../components/ImportMembersModal';
import EditMemberModal from '../components/EditMemberModal';
import AddMemberModal from '../components/AddMemberModal';
// import CSVImportTester from '../components/CSVImportTester';
import { 
  UserIcon, 
  EnvelopeIcon, 
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowUpIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const AdminMembers: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingLinks, setSendingLinks] = useState(false);
  const [linkResults, setLinkResults] = useState<{success: number, errors: string[]} | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [generatingLink, setGeneratingLink] = useState<string | null>(null);
  const [showTester, setShowTester] = useState(false);
  // const [showCSVTester, setShowCSVTester] = useState(false);
  
  // États pour la sélection multiple
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        const membersData = await MemberService.getAllMembers();
        setMembers(membersData);
      } catch (error) {
        console.error('Erreur lors du chargement des membres:', error);
        setError('Erreur lors du chargement des membres');
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, []);

  const filteredMembers = members.filter(member => {
    // Filtrage par statut
    if (filter === 'active' && member.membershipStatus !== 'active') return false;
    if (filter === 'inactive' && member.membershipStatus === 'active') return false;
    
    // Filtrage par recherche
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
      const email = member.email.toLowerCase();
      const department = (member.department || '').toLowerCase();
      
      if (!fullName.includes(searchLower) && 
          !email.includes(searchLower) && 
          !department.includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  const handleSendMagicLinks = async () => {
    setSendingLinks(true);
    setLinkResults(null);
    
    try {
      const activeMembers = members.filter(m => m.membershipStatus === 'active' && m.hasVotingRights);
      const results = await EmailService.sendMagicLinksToAllMembers(activeMembers);
      setLinkResults(results);
    } catch (error) {
      console.error('Erreur lors de l\'envoi des liens:', error);
      setError('Erreur lors de l\'envoi des liens magiques');
    } finally {
      setSendingLinks(false);
    }
  };

  const handleImportComplete = async (importedCount: number) => {
    // Recharger la liste des membres après l'import
    try {
      const membersData = await MemberService.getAllMembers();
      setMembers(membersData);
    } catch (error) {
      console.error('Erreur lors du rechargement des membres:', error);
    }
  };

  const handleEditMember = (member: Member) => {
    setSelectedMember(member);
    setShowEditModal(true);
  };

  const handleMemberAdded = async () => {
    // Recharger la liste des membres
    try {
      const membersData = await MemberService.getAllMembers();
      setMembers(membersData);
    } catch (error) {
      console.error('Erreur lors du rechargement des membres:', error);
    }
  };

  const handleMemberUpdated = (updatedMember: Member) => {
    setMembers(prev => prev.map(member => 
      member.id === updatedMember.id ? updatedMember : member
    ));
  };

  const handleGenerateMagicLink = async (member: Member) => {
    setGeneratingLink(member.id);
    
    try {
      // Générer le lien magique individuel (utiliser l'email comme ID)
      const magicLink = await MagicLinkService.generateIndividualMagicLink(member.email, member.email);
      
      // Copier le lien dans le presse-papiers
      await navigator.clipboard.writeText(magicLink);
      
      // Afficher une notification de succès
      alert(`Lien magique généré et copié pour ${member.firstName} ${member.lastName}!\n\nLe lien a été copié dans le presse-papiers.`);
      
    } catch (error) {
      console.error('Erreur lors de la génération du lien magique:', error);
      alert('Erreur lors de la génération du lien magique');
    } finally {
      setGeneratingLink(null);
    }
  };

  // Fonctions pour la sélection multiple
  const toggleSelectMember = (memberId: string) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const selectAllMembers = () => {
    setSelectedMembers(new Set(filteredMembers.map(m => m.id)));
  };

  const deselectAllMembers = () => {
    setSelectedMembers(new Set());
  };

  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const deletePromises = Array.from(selectedMembers).map(memberId => 
        MemberService.deleteMember(memberId)
      );
      await Promise.all(deletePromises);
      
      // Recharger les membres
      const membersData = await MemberService.getAllMembers();
      setMembers(membersData);
      setSelectedMembers(new Set());
      setShowDeleteConfirm(false);
      alert(`${selectedMembers.size} membre(s) supprimé(s) avec succès`);
    } catch (error) {
      console.error('Erreur lors de la suppression des membres:', error);
      alert('Erreur lors de la suppression des membres');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    setBulkActionLoading(true);
    try {
      const updatePromises = Array.from(selectedMembers).map(memberId => {
        const member = members.find(m => m.id === memberId);
        if (member) {
          return MemberService.updateMemberStatus(member.email, 'active');
        }
        return Promise.resolve();
      });
      await Promise.all(updatePromises);
      
      // Recharger les membres
      const membersData = await MemberService.getAllMembers();
      setMembers(membersData);
      setSelectedMembers(new Set());
      alert(`${selectedMembers.size} membre(s) activé(s) avec succès`);
    } catch (error) {
      console.error('Erreur lors de l\'activation des membres:', error);
      alert('Erreur lors de l\'activation des membres');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    setBulkActionLoading(true);
    try {
      const updatePromises = Array.from(selectedMembers).map(memberId => {
        const member = members.find(m => m.id === memberId);
        if (member) {
          return MemberService.updateMemberStatus(member.email, 'inactive');
        }
        return Promise.resolve();
      });
      await Promise.all(updatePromises);
      
      // Recharger les membres
      const membersData = await MemberService.getAllMembers();
      setMembers(membersData);
      setSelectedMembers(new Set());
      alert(`${selectedMembers.size} membre(s) désactivé(s) avec succès`);
    } catch (error) {
      console.error('Erreur lors de la désactivation des membres:', error);
      alert('Erreur lors de la désactivation des membres');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleToggleMemberStatus = async (memberEmail: string, currentStatus: string) => {
    try {
      let newStatus: 'active' | 'inactive' | 'paused';
      
      if (currentStatus === 'active') {
        newStatus = 'inactive';
      } else if (currentStatus === 'inactive') {
        newStatus = 'paused';
      } else {
        newStatus = 'active';
      }

      await MemberService.updateMemberStatus(memberEmail, newStatus);
      
      // Mettre à jour la liste locale
      setMembers(prev => prev.map(member => 
        member.email === memberEmail 
          ? { ...member, membershipStatus: newStatus, hasVotingRights: newStatus === 'active' }
          : member
      ));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      setError('Erreur lors de la mise à jour du statut du membre');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'inactive':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'paused':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <UserIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Erreur</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Membres</h1>
            <p className="mt-2 text-gray-600">
              Gérez les membres de l'association ASPROCOM
            </p>
          </div>
                 <div className="flex items-center space-x-3">
                   <button
                     onClick={() => setShowAddModal(true)}
                     className="btn-secondary flex items-center"
                   >
                     <UserIcon className="w-5 h-5 mr-2" />
                     Ajouter un membre
                   </button>
                   {/* <button
                     onClick={() => setShowTester(true)}
                     className="btn-secondary flex items-center"
                   >
                     <LinkIcon className="w-5 h-5 mr-2" />
                     Tester les liens
                   </button> */}
                   {/* <button
                     onClick={() => setShowCSVTester(true)}
                     className="btn-secondary flex items-center"
                   >
                     <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
                     Tester CSV
                   </button> */}
                   <button
                     onClick={() => setShowImportModal(true)}
                     className="btn-secondary flex items-center"
                   >
                     <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
                     Importer CSV
                   </button>
                   <button
                     onClick={handleSendMagicLinks}
                     disabled={sendingLinks}
                     className="btn-primary flex items-center"
                   >
                     {sendingLinks ? (
                       <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                     ) : (
                       <EnvelopeIcon className="w-5 h-5 mr-2" />
                     )}
                     {sendingLinks ? 'Envoi en cours...' : 'Envoyer les liens de vote'}
                   </button>
                 </div>
        </div>
      </div>

      {/* Filtres et Recherche */}
      <div className="mb-6 space-y-4">
        {/* Barre de recherche */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher par nom, email ou département..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        {/* Filtres par statut */}
        <div className="flex space-x-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              filter === 'all'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tous ({filteredMembers.length})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              filter === 'active'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Actifs ({filteredMembers.filter(m => m.membershipStatus === 'active').length})
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              filter === 'inactive'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Inactifs ({filteredMembers.filter(m => m.membershipStatus !== 'active').length})
          </button>
        </div>
      </div>

      {/* Indicateur de résultats de recherche */}
      {searchTerm.trim() && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            🔍 Recherche pour "<span className="font-medium">{searchTerm}</span>" : 
            <span className="font-medium ml-1">{filteredMembers.length} résultat(s) trouvé(s)</span>
            {filteredMembers.length === 0 && (
              <span className="ml-2 text-blue-600">Aucun membre ne correspond à votre recherche</span>
            )}
          </p>
        </div>
      )}

      {/* Résultats d'envoi des liens magiques */}
      {linkResults && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">Résultats de l'envoi des liens</h3>
          <p className="text-sm text-blue-700">
            ✅ {linkResults.success} liens envoyés avec succès
            {linkResults.errors.length > 0 && (
              <span className="ml-2">❌ {linkResults.errors.length} erreurs</span>
            )}
          </p>
          {linkResults.errors.length > 0 && (
            <details className="mt-2">
              <summary className="text-sm text-blue-600 cursor-pointer">Voir les erreurs</summary>
              <ul className="mt-2 text-sm text-red-600">
                {linkResults.errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Barre d'actions en lot */}
      {selectedMembers.size > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedMembers.size} membre(s) sélectionné(s)
              </span>
              <button
                onClick={deselectAllMembers}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Tout désélectionner
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkActivate}
                disabled={bulkActionLoading}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Activer
              </button>
              <button
                onClick={handleBulkDeactivate}
                disabled={bulkActionLoading}
                className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 disabled:opacity-50"
              >
                Désactiver
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={bulkActionLoading}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des membres */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {/* En-tête avec sélection */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center">
          <input
            type="checkbox"
            checked={selectedMembers.size === filteredMembers.length && filteredMembers.length > 0}
            onChange={(e) => {
              if (e.target.checked) {
                selectAllMembers();
              } else {
                deselectAllMembers();
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-3 text-sm font-medium text-gray-700">
            Sélectionner tout
          </label>
        </div>

        <ul className="divide-y divide-gray-200">
          {filteredMembers.map((member) => (
            <li key={member.id}>
              <div className="px-4 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  {/* Case à cocher */}
                  <input
                    type="checkbox"
                    checked={selectedMembers.has(member.id)}
                    onChange={() => toggleSelectMember(member.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4"
                  />
                  
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-gray-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </p>
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(member.membershipStatus)}`}>
                        {member.membershipStatus}
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-500">{member.email}</p>
                    </div>
                    <div className="flex items-center mt-1">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-500">
                        Membre depuis {member.memberSince.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    {getStatusIcon(member.membershipStatus)}
                    <span className="ml-2 text-sm text-gray-500">
                      {member.hasVotingRights ? 'Droit de vote' : 'Pas de droit de vote'}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditMember(member)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                      title="Modifier le membre"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => handleGenerateMagicLink(member)}
                      disabled={generatingLink === member.id}
                      className="p-2 text-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors duration-200"
                      title="Générer et copier le lien magique"
                    >
                      {generatingLink === member.id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleToggleMemberStatus(member.email, member.membershipStatus)}
                      className={`p-2 rounded-md transition-colors duration-200 ${
                        member.membershipStatus === 'active'
                          ? 'text-red-600 hover:bg-red-50'
                          : member.membershipStatus === 'inactive'
                          ? 'text-yellow-600 hover:bg-yellow-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={
                        member.membershipStatus === 'active'
                          ? 'Désactiver le membre'
                          : member.membershipStatus === 'inactive'
                          ? 'Mettre en pause'
                          : 'Activer le membre'
                      }
                    >
                      {member.membershipStatus === 'active' ? (
                        <StopIcon className="w-5 h-5" />
                      ) : member.membershipStatus === 'inactive' ? (
                        <PauseIcon className="w-5 h-5" />
                      ) : (
                        <PlayIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      Dernière activité: {member.lastActive.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {filteredMembers.length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun membre trouvé</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucun membre ne correspond aux critères sélectionnés.
          </p>
        </div>
      )}

      {/* Modal d'import CSV */}
      <ImportMembersModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={handleImportComplete}
      />

      {/* Modal d'édition des membres */}
      <EditMemberModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
        onMemberUpdated={handleMemberUpdated}
      />

      {/* Modal de test des liens magiques */}
      {showTester && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-800 bg-opacity-75 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Test des Liens Magiques</h2>
              <button
                onClick={() => setShowTester(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-800 bg-opacity-75 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Êtes-vous sûr de vouloir supprimer {selectedMembers.size} membre(s) ? 
                Cette action est irréversible.
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={bulkActionLoading}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {bulkActionLoading ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      <AddMemberModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleMemberAdded}
      />

      <ImportMembersModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={handleMemberAdded}
      />

      <EditMemberModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onMemberUpdated={handleMemberUpdated}
        member={selectedMember}
      />

      {/* Testeur CSV 
      {showCSVTester && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Testeur d'Import CSV</h2>
                <button
                  onClick={() => setShowCSVTester(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <CSVImportTester />
            </div>
          </div>
        </div>
      )}*/}
    </div>
  );
};

export default AdminMembers;
